import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api";
import { workOrdersApi } from "../lib/apiSubcontractors";
import { WorkOrderForm } from "../components/subcontractors/WorkOrderForm";
import { WorkOrderReadiness } from "../components/jobs/JobWorkspace";
import { ResponsiveTable } from "../components/common/ResponsiveTable";
import type { WorkOrderFormData } from "../types/subcontractors";
const fail = (e: unknown) => {
  const err = e as { responseData?: unknown; message?: string };
  const data = err.responseData;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(" ");
  if (data && typeof data === "object") {
    const fields = data as Record<string, unknown>;
    for (const key of ["delivery_error", "detail", "error"]) {
      if (typeof fields[key] === "string" && fields[key]) return fields[key] as string;
    }
    const messages = Object.entries(fields).filter(([, value]) => typeof value === "string" || Array.isArray(value));
    if (messages.length) return messages.map(([field, value]) => `${field.replaceAll("_", " ")}: ${Array.isArray(value) ? value.join(" ") : value}`).join(" · ");
  }
  return err.message || "Unable to save. Please retry.";
};
interface Activity {
  id: number;
  at: string;
  title: string;
  actor: string;
  version: number;
  before?: unknown;
  after?: unknown;
  reason?: string;
  filename?: string;
  delivery_state?: string;
  notes?: string;
}
export default function WorkOrderDetail() {
  const { id } = useParams(),
    navigate = useNavigate(),
    client = useQueryClient();
  const [modal, setModal] = useState(""),
    [notes, setNotes] = useState(""),
    [rating, setRating] = useState<number | null>(null),
    [done, setDone] = useState<number[]>([]),
    [retry, setRetry] = useState(false),
    [requestId, setRequestId] = useState(""),
    [reviewVersion, setReviewVersion] = useState(0),
    [revision, setRevision] = useState<Record<string, string>>({}),
    [reviewed, setReviewed] = useState(false),
    [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ["work-order", id],
    queryFn: () => workOrdersApi.get(Number(id)),
    refetchInterval: (q) =>
      q.state.data?.delivery_state === "sending" ? 5000 : false,
  });
  const activity = useQuery({
    queryKey: ["work-order-activity", id, page],
    queryFn: async () =>
      (
        await apiClient.get<{ results: Activity[]; count: number }>(
          `/api/subcontractor-work-orders/${id}/activity/?page=${page}`,
        )
      ).data,
  });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ["work-order", id] });
    client.invalidateQueries({ queryKey: ["work-orders"] });
    client.invalidateQueries({ queryKey: ["work-order-activity", id] });
  };
  const action = useMutation({
    mutationFn: ({ name, data = {} }: { name: string; data?: unknown }) =>
      apiClient.post(`/api/subcontractor-work-orders/${id}/${name}/`, data),
    onSuccess: () => setModal(""),
    onError: (_, variables) => { if (variables.name === "send") setRequestId(crypto.randomUUID()); },
    onSettled: refresh,
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const data = new FormData();
      data.append("file", file);
      return apiClient.post(
        `/api/subcontractor-work-orders/${id}/upload-evidence/`,
        data,
      );
    },
    onSuccess: refresh,
  });
  if (query.isLoading)
    return <Typography sx={{ p: 3 }}>Loading work order…</Typography>;
  if (query.isError || !query.data)
    return (
      <Alert color="danger">
        Unable to load order.{" "}
        <Button onClick={() => query.refetch()}>Retry</Button>
      </Alert>
    );
  const order = query.data,
    terminal = [
      "completed",
      "invoiced",
      "paid",
      "cancelled",
      "rejected",
    ].includes(order.status),
    dispatchable =
      !terminal &&
      order.delivery_state !== "sending" &&
      (order.sent_version !== order.version || order.delivery_state !== "sent");
  const copy: Partial<WorkOrderFormData> = {
    job: order.job,
    subcontractor: order.subcontractor,
    title: `Copy of ${order.title}`,
    description: order.description,
    scope_of_work: order.scope_of_work,
    special_instructions: order.special_instructions,
    checklist: order.checklist?.map((i) => ({ ...i, done: false })),
    start_date: "",
    end_date: "",
    agreed_cost: 0,
  };
  const open = (name: string) => {
    setReviewVersion(order.version);
    setModal(name);
    setNotes("");
    setReviewed(false);
    action.reset();
    if (name === "send") {
      setRequestId(crypto.randomUUID());
      setRetry(false);
    }
    if (name === "complete") {
      setRating(null);
      setDone([]);
    }
    if (name === "revise")
      setRevision({
        title: order.title,
        scope_of_work: order.scope_of_work,
        start_date: order.start_date,
        end_date: order.end_date,
        agreed_cost: String(order.agreed_cost),
        special_instructions: order.special_instructions || "",
      });
  };
  return (
    <Stack
      spacing={2}
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1300,
        mx: "auto",
        overflowWrap: "anywhere",
      }}
    >
      <Button
        variant="plain"
        sx={{ alignSelf: "flex-start" }}
        onClick={() => navigate("/operations/work-orders")}
      >
        Back to work orders
      </Button>
      <Typography level="h2">
        {order.work_order_number} · {order.title}
      </Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        <Chip>{order.status.replaceAll("_", " ")}</Chip>
        <Chip>Version {order.version}</Chip>
        <Chip>Last sent version {order.sent_version || "none"}</Chip>
      </Stack>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        <Button
          component={Link}
          to={`/operations/jobs/${order.job}`}
          variant="outlined"
        >
          Job {order.job_details?.job_number}
        </Button>
        <Button
          component={Link}
          to={`/operations/subcontractors/${order.subcontractor}`}
          variant="outlined"
        >
          {order.subcontractor_details?.company_name || "Subcontractor profile"}
        </Button>
      </Stack>
      <Card>
        <Typography>{order.job_details?.address}</Typography>
        <Typography>
          {order.start_date} – {order.end_date} · Agreed $
          {Number(order.agreed_cost).toFixed(2)} · Total $
          {Number(order.total_cost).toFixed(2)}
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {order.scope_of_work}
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {order.special_instructions}
        </Typography>
        <WorkOrderReadiness order={order} />
      </Card>
      <Alert color={order.delivery_state === "sent" ? "success" : "warning"}>
        Delivery:{" "}
        {order.delivery_state === "sent"
          ? "Email provider accepted"
          : order.delivery_state === "legacy"
            ? "Previous dispatch; confirmation was not recorded"
            : order.delivery_state.replaceAll("_", " ")}
        . {order.delivery_error}{" "}
        {order.sent_version !== order.version && order.sent_version > 0
          ? "Current revision has not been sent."
          : ""}
      </Alert>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        {dispatchable && (
          <Button onClick={() => open("send")}>Review dispatch</Button>
        )}
        {order.delivery_state === "sending" && (
          <Button
            variant="outlined"
            onClick={() => action.mutate({ name: "recover-dispatch" })}
          >
            Recover interrupted dispatch (after 10 minutes)
          </Button>
        )}
        {order.status === "draft" && (
          <Button variant="outlined" onClick={() => open("edit")}>
            Edit draft
          </Button>
        )}
        {["sent", "accepted", "in_progress"].includes(order.status) && (
          <Button variant="outlined" onClick={() => open("revise")}>
            Review revision
          </Button>
        )}
        <Button variant="outlined" onClick={() => open("duplicate")}>
          Duplicate as draft
        </Button>
        {order.status === "sent" && (
          <>
            <Button
              disabled={action.isPending}
              onClick={() => action.mutate({ name: "accept" })}
            >
              Record acceptance
            </Button>
            <Button
              variant="outlined"
              disabled={action.isPending}
              onClick={() => action.mutate({ name: "reject" })}
            >
              Record rejection
            </Button>
          </>
        )}
        {order.status === "accepted" && (
          <Button
            disabled={action.isPending}
            onClick={() => action.mutate({ name: "start" })}
          >
            Start work
          </Button>
        )}
        {order.status === "in_progress" && (
          <Button onClick={() => open("complete")}>Complete work</Button>
        )}
        {!["completed", "invoiced", "paid", "cancelled"].includes(
          order.status,
        ) && (
          <Button
            color="danger"
            variant="outlined"
            onClick={() => open("cancel")}
          >
            Cancel order
          </Button>
        )}
      </Stack>
      {action.isError && <Alert color="danger">{fail(action.error)}</Alert>}
      <Card>
        <Typography level="title-lg">Completion evidence</Typography>
        {order.checklist?.map((item, i) => (
          <Typography key={i}>
            {item.done ? "✓" : "○"} {item.label}
          </Typography>
        ))}
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {order.completion_notes || "No completion notes recorded."}
        </Typography>
        <Typography>
          Quality:{" "}
          {order.quality_rating == null
            ? "No rating provided"
            : `${order.quality_rating}/5`}
        </Typography>
        <Button component="label" variant="outlined" loading={upload.isPending}>
          Add photo or file
          <input
            hidden
            type="file"
            aria-label="Upload work evidence"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = "";
            }}
          />
        </Button>
        <Typography level="body-xs">
          PDF, PNG or JPEG · Up to 20 MB · Filed against this work order and its
          job.
        </Typography>
        {order.evidence?.map((e) => (
          <Box key={e.id}>
            <Button
              component="a"
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="plain"
            >
              {e.filename}
            </Button>
            <Typography level="body-sm">
              {e.uploaded_by} · {new Date(e.uploaded_at).toLocaleString()}
            </Typography>
          </Box>
        ))}
        {upload.isError && <Alert color="danger">{fail(upload.error)}</Alert>}
      </Card>
      <Card>
        <Typography level="title-lg">Related invoices</Typography>
        <ResponsiveTable
          rows={order.invoices || []}
          getRowKey={(r: any) => r.id}
          columns={[
            {
              key: "number",
              header: "Invoice",
              mobileVariant: "header",
              render: (r: any) => (
                <Button
                  component={Link}
                  to={`/operations/subcontractors/${order.subcontractor}`}
                  variant="plain"
                >
                  {r.invoice_number}
                </Button>
              ),
            },
            {
              key: "amount",
              header: "Amount",
              render: (r: any) => (
                <Typography>${Number(r.total_amount).toFixed(2)}</Typography>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (r: any) => <Chip>{r.status}</Chip>,
            },
          ]}
        />
      </Card>
      <Card>
        <Typography level="title-lg">Dispatch & lifecycle history</Typography>
        {activity.data?.results.map((e) => (
          <Card key={e.id} variant="soft">
            <Typography level="title-md">
              {e.title} · Version {e.version}
            </Typography>
            <Typography level="body-sm">
              {e.actor} · {new Date(e.at).toLocaleString()}
            </Typography>
            {e.reason && <Typography>{e.reason}</Typography>}
            {e.filename && <Typography>{e.filename}</Typography>}
            {e.before != null && (
              <Typography level="body-sm">
                Before: {JSON.stringify(e.before)}
              </Typography>
            )}
            {e.after != null && (
              <Typography level="body-sm">
                After: {JSON.stringify(e.after)}
              </Typography>
            )}
          </Card>
        ))}
        {activity.isError && (
          <Alert color="danger">Unable to load history.</Alert>
        )}
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Typography>Page {page}</Typography>
          <Button
            disabled={page * 20 >= (activity.data?.count || 0)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Stack>
      </Card>
      <Modal open={!!modal} onClose={() => !action.isPending && setModal("")}>
        <ModalDialog
          sx={{
            width: "min(850px,95vw)",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">
            {
              (
                {
                  send: "Review dispatch",
                  edit: "Edit draft",
                  duplicate: "Duplicate as draft",
                  revise: "Review scope and cost revision",
                  complete: "Complete work",
                  cancel: "Cancel work order",
                } as Record<string, string>
              )[modal]
            }
          </Typography>
          {(modal === "edit" || modal === "duplicate") && (
            <WorkOrderForm
              workOrder={modal === "edit" ? order : undefined}
              initialValues={modal === "duplicate" ? copy : undefined}
              onCancel={() => setModal("")}
              onSuccess={(record) => {
                setModal("");
                refresh();
                if (record?.id)
                  navigate(`/operations/work-orders/${record.id}`);
              }}
            />
          )}
          {modal === "send" && (
            <>
              <Typography>
                Send version {order.version} to{" "}
                {order.subcontractor_details?.email}. The email includes scope,
                dates, cost and site instructions.
              </Typography>
              {order.delivery_state === "unknown" && (
                <Checkbox
                  label="I checked the prior delivery and approve retrying this email"
                  checked={retry}
                  onChange={(e) => setRetry(e.target.checked)}
                />
              )}
              <Button
                loading={action.isPending}
                disabled={order.delivery_state === "unknown" && !retry}
                onClick={() =>
                  action.mutate({
                    name: "send",
                    data: {
                      request_id: requestId,
                      confirm_retry: retry,
                      expected_version: reviewVersion,
                    },
                  })
                }
              >
                Send reviewed version
              </Button>
            </>
          )}
          {modal === "complete" && (
            <>
              <FormControl>
                <FormLabel>Completion notes (optional)</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Quality rating (optional)</FormLabel>
                <Select
                  value={rating || 0}
                  onChange={(_, v) => setRating(v || null)}
                >
                  <Option value={0}>No rating</Option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Option key={n} value={n}>
                      {n} / 5
                    </Option>
                  ))}
                </Select>
              </FormControl>
              {order.checklist?.map((item, i) => (
                <Checkbox
                  key={i}
                  label={item.label}
                  checked={done.includes(i)}
                  onChange={(e) =>
                    setDone(
                      e.target.checked
                        ? [...done, i]
                        : done.filter((v) => v !== i),
                    )
                  }
                />
              ))}
              <Button
                loading={action.isPending}
                onClick={() =>
                  action.mutate({
                    name: "complete",
                    data: {
                      notes,
                      ...(rating ? { quality_rating: rating } : {}),
                      completed_items: done,
                    },
                  })
                }
              >
                Confirm completion
              </Button>
            </>
          )}
          {modal === "revise" && (
            <>
              <Alert>
                Changes create a new version. Dispatch the revised version
                before continuing work.
              </Alert>
              {Object.entries(revision).map(([key, value]) => (
                <FormControl key={key}>
                  <FormLabel>{key.replaceAll("_", " ")}</FormLabel>
                  <Input
                    type={
                      key.endsWith("_date")
                        ? "date"
                        : key === "agreed_cost"
                          ? "number"
                          : "text"
                    }
                    value={value}
                    onChange={(e) => {
                      setReviewed(false);
                      setRevision({ ...revision, [key]: e.target.value });
                    }}
                  />
                  <Typography level="body-xs">
                    Before: {String((order as any)[key] || "—")}
                  </Typography>
                </FormControl>
              ))}
              <FormControl>
                <FormLabel>Revision approval reason</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </FormControl>
              <Checkbox
                label="I reviewed and approve these scope, date and cost changes"
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
              />
              <Button
                loading={action.isPending}
                disabled={!reviewed || !notes.trim()}
                onClick={() =>
                  action.mutate({
                    name: "revise",
                    data: {
                      expected_version: reviewVersion,
                      approved: true,
                      reason: notes,
                      changes: revision,
                    },
                  })
                }
              >
                Approve revision
              </Button>
            </>
          )}
          {modal === "cancel" && (
            <>
              <Typography>
                Cancel this order and retain its work history?
              </Typography>
              <Button
                color="danger"
                loading={action.isPending}
                onClick={() => action.mutate({ name: "cancel" })}
              >
                Confirm cancellation
              </Button>
            </>
          )}
          {action.isError && <Alert color="danger">{fail(action.error)}</Alert>}
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
